import React from "react";
import { Card, Timeline, Typography, Badge, Avatar, Space, Empty, Tooltip } from "antd";
import {
  SendOutlined,
  MailOutlined,
  EyeOutlined,
  LinkOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";

const { Text, Title } = Typography;

const SequenceActivityFeed = ({ activityData }) => {
  if (!activityData || !activityData.length) {
    return (
      <Card
        title={
          <Space>
            <ClockCircleOutlined style={{ color: "#1890ff" }} />
            <Title level={4} style={{ margin: 0 }}>Recent Activity</Title>
          </Space>
        }
        style={{ borderRadius: "12px", marginBottom: "20px" }}
      >
        <Empty
          description="No recent activity"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: "40px 0" }}
        />
      </Card>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "sent": return "#52c41a";
      case "failed": return "#f5222d";
      case "queued": return "#1890ff";
      case "delivered": return "#52c41a";
      case "bounced": return "#f5222d";
      case "rejected": return "#f5222d";
      default: return "#d9d9d9";
    }
  };

  const getStatusIcon = (status, engagement) => {
    if (engagement?.clicked) {
      return <LinkOutlined style={{ color: "#722ed1" }} />;
    }
    if (engagement?.opened) {
      return <EyeOutlined style={{ color: "#1890ff" }} />;
    }
    
    switch (status) {
      case "sent":
      case "delivered":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "failed":
      case "bounced":
      case "rejected":
        return <ExclamationCircleOutlined style={{ color: "#f5222d" }} />;
      default:
        return <SendOutlined style={{ color: "#1890ff" }} />;
    }
  };

  const getActivityDescription = (activity) => {
    const { userEmail, userName, sequenceName, emailIndex, status, engagement } = activity;
    
    let description = "";
    let actionText = "";
    
    if (engagement?.clicked) {
      actionText = "clicked email";
      description = `clicked on links in Email #${emailIndex + 1}`;
    } else if (engagement?.opened) {
      actionText = "opened email";
      description = `opened Email #${emailIndex + 1}`;
    } else {
      switch (status) {
        case "sent":
          actionText = "sent email";
          description = `sent Email #${emailIndex + 1}`;
          break;
        case "delivered":
          actionText = "delivered email";
          description = `delivered Email #${emailIndex + 1}`;
          break;
        case "failed":
          actionText = "failed to send";
          description = `failed to send Email #${emailIndex + 1}`;
          break;
        case "bounced":
          actionText = "bounced email";
          description = `bounced Email #${emailIndex + 1}`;
          break;
        default:
          actionText = "processed email";
          description = `processed Email #${emailIndex + 1}`;
      }
    }

    return {
      title: `${userName || userEmail} ${actionText}`,
      description: `${description} from "${sequenceName}" sequence`,
      actionText
    };
  };

  const formatTimeAgo = (date) => {
    if (!date) return "N/A";
    const momentDate = moment(date);
    return momentDate.isValid() ? momentDate.fromNow() : "N/A";
  };

  const getUserInitials = (name, email) => {
    if (name && typeof name === 'string' && name.includes(' ')) {
      const parts = name.split(' ');
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    if (name && typeof name === 'string' && name.length >= 2) {
      return name.substring(0, 2).toUpperCase();
    }
    if (name && typeof name === 'string' && name.length === 1) {
      return name.toUpperCase();
    }
    if (email && typeof email === 'string' && email.length >= 2) {
      return email.substring(0, 2).toUpperCase();
    }
    return "?";
  };

  return (
    <Card
      title={
        <Space>
          <ClockCircleOutlined style={{ color: "#1890ff" }} />
          <Title level={4} style={{ margin: 0 }}>Recent Activity</Title>
        </Space>
      }
      style={{ borderRadius: "12px", marginBottom: "20px" }}
      bodyStyle={{ padding: "24px", maxHeight: "600px", overflowY: "auto" }}
    >
      <Timeline mode="left">
        {activityData.map((activity, index) => {
          const activityInfo = getActivityDescription(activity);
          const timeAgo = formatTimeAgo(activity.sentAt);
          
          return (
            <Timeline.Item
              key={index}
              dot={
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  background: "#fff",
                  border: `2px solid ${getStatusColor(activity.status)}`,
                }}>
                  {getStatusIcon(activity.status, activity.engagement)}
                </div>
              }
              color={getStatusColor(activity.status)}
            >
              <div style={{ marginLeft: "8px" }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                  <Avatar 
                    size="small" 
                    style={{ 
                      backgroundColor: getStatusColor(activity.status),
                      marginRight: "8px",
                      fontSize: "10px"
                    }}
                  >
                    {getUserInitials(activity.userName, activity.userEmail)}
                  </Avatar>
                  
                  <Space direction="vertical" size={0}>
                    <Text strong style={{ fontSize: "14px" }}>
                      {activityInfo.title}
                    </Text>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      {activityInfo.description}
                    </Text>
                  </Space>
                </div>
                
                <div style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center",
                  marginTop: "8px"
                }}>
                  <div>
                    <Badge 
                      status={activity.status === "sent" ? "success" : 
                             activity.status === "failed" ? "error" : "processing"}
                      text={activity.status.charAt(0).toUpperCase() + activity.status.slice(1)}
                    />
                    
                    {activity.engagement?.opened && (
                      <Badge 
                        status="processing" 
                        text="Opened"
                        style={{ marginLeft: "8px" }}
                      />
                    )}
                    
                    {activity.engagement?.clicked && (
                      <Badge 
                        status="success" 
                        text="Clicked"
                        style={{ marginLeft: "8px" }}
                      />
                    )}
                  </div>
                  
                  <Tooltip title={activity.sentAt && moment(activity.sentAt).isValid() ? moment(activity.sentAt).format("YYYY-MM-DD HH:mm:ss") : "No date available"}>
                    <Text type="secondary" style={{ fontSize: "11px" }}>
                      {timeAgo}
                    </Text>
                  </Tooltip>
                </div>
              </div>
            </Timeline.Item>
          );
        })}
      </Timeline>
      
      {activityData.length >= 50 && (
        <div style={{ textAlign: "center", paddingTop: "16px", borderTop: "1px solid #f0f0f0" }}>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Showing last 50 activities
          </Text>
        </div>
      )}
    </Card>
  );
};

export default SequenceActivityFeed;